import Footer, { type FooterProps } from 'components/Footer';
import type { HeaderNav } from 'interfaces/navigation/Header';
import Header from 'components/Header';
import { LayoutWrapper, LeaderboardAdWrapper, NativoLeaderboard } from 'components/Layout/LayoutStyles';
import { GoogleAnalytics } from '@next/third-parties/google';

import { Leaderboard } from 'components/Ad/AdSlot';
import type { PageType } from 'interfaces/content/articles/Post';
import RecommendsFooter, { type RecommendsFooterProps } from 'components/RecommendsFooter';
import { Microsites } from 'constants/constants';
import type { Hub } from 'interfaces/Home';
import { useRouter } from 'next/router';
import type { JSX } from 'react';
import PaywallaxMarkII from 'components/PaywallaxMarkII';
import { updateLocalStorageObject } from 'utils/localStorageUtils';

interface LayoutProps {
  children: JSX.Element;
  headerNav: HeaderNav;
  leaderboard: boolean;
  footer?: FooterProps | RecommendsFooterProps;
  hasVanityUrl: boolean | undefined;
  pageType: PageType;
  currentSection: string;
  section?: Hub;
  currentTags: string[];
  currentAuthors?: string;
  hideHeaderFooter?: boolean;
}

const Layout = (props: LayoutProps) => {
  const { children, leaderboard, footer, headerNav, hasVanityUrl, section, currentTags, hideHeaderFooter } = props;

  const router = useRouter();

  const isRecommends = [Microsites.Recommends, Microsites.Education].includes(headerNav?.subDomain);
  const showRecommendsFooter = process.env.FEATURE_FLAG_RECOMMENDS_FOOTER && isRecommends;

  const isPianoTemplate = ['/subscribe/', '/paid-subscribe', '/email-subscribe/'].some((path) =>
    router.asPath.includes(path),
  );

  if (isPianoTemplate) {
    const paths = router.asPath.split('?');
    const queryString = new URLSearchParams(paths[1] || '').toString();
    updateLocalStorageObject('checkoutTrackingProps', { checkout_referral_params: queryString });
  }

  if (hideHeaderFooter || isPianoTemplate) {
    return children;
  }

  return (
    <div>
      <GoogleAnalytics gaId={process.env.G_ANALYTICS_ID || ''} />
      {leaderboard && (
        <LeaderboardAdWrapper className='leaderboard-ad-wrapper-parent'>
          <Leaderboard id={0} />
        </LeaderboardAdWrapper>
      )}
      {hasVanityUrl && <NativoLeaderboard className='nativo-content__leaderboard' />}
      {headerNav && !isPianoTemplate && (
        <Header
          {...headerNav}
          topics={headerNav.topicNavigation}
          currentTags={currentTags}
        />
      )}
      <LayoutWrapper
        $isRecommends={isRecommends}
        $showRecommendsFooter={showRecommendsFooter}
        $isPianoTemplate={isPianoTemplate}
      >
        <main>{children}</main>
        {!showRecommendsFooter && footer && !isPianoTemplate && <Footer {...(footer as FooterProps)} />}
        {showRecommendsFooter && footer && (
          <RecommendsFooter
            {...(footer as RecommendsFooterProps)}
            currentSection={section}
          />
        )}
      </LayoutWrapper>
      <PaywallaxMarkII />
    </div>
  );
};

export default Layout;
